import type { QuestionAnswerDistributionType } from 'types/api/diagnosisReportRefResponseType';

export type PatternDetailItem = {
  questionTitle: string;
  optionTitle: string;
  optionNo: number;
};

/**
 * パターン文字列 "A-B-C" を質問文・選択肢テキストの配列に展開するクロージャを返す。
 * questions が無し / 空の場合は常に null を返す関数を返す。
 * questions 有りの場合は sortedQuestions を一度だけ算出してクロージャに閉じ込め、
 * 各呼び出しで再ソートしない（移行元 useMemo の memo 構造を保持）。
 */
export function buildPatternExpander(
  questions: QuestionAnswerDistributionType[] | null | undefined
): (pattern: string) => PatternDetailItem[] | null {
  if (!questions || questions.length === 0) return () => null;

  const sortedQuestions = [...questions].sort((a, b) => a.questionNo - b.questionNo);

  return (pattern: string): PatternDetailItem[] | null => {
    const letters = pattern.split('-');
    if (letters.length !== sortedQuestions.length) return null;

    const result: PatternDetailItem[] = [];
    for (let i = 0; i < letters.length; i++) {
      const letter = letters[i];
      const question = sortedQuestions[i];
      if (!question) continue;
      const optionIndex = letter.charCodeAt(0) - 65;
      const sortedOptions = [...question.options].sort((a, b) => a.optionNo - b.optionNo);
      const option = sortedOptions[optionIndex];
      const questionTitle = question.questionTitle ?? `Q${question.questionNo}`;
      const optionTitle = option?.optionTitle ?? letter;
      const optionNo = option?.optionNo ?? optionIndex + 1;
      result.push({ questionTitle, optionTitle, optionNo });
    }
    return result;
  };
}

/**
 * 質問別分布情報なし / 長不一致の場合のフォールバック整形。
 * "A-B-C" → "Q1:A → Q2:B → Q3:C"
 */
export function formatPatternFallback(pattern: string): string {
  return pattern
    .split('-')
    .map((letter, i) => `Q${i + 1}:${letter}`)
    .join(' → ');
}
